import { verifyAddress } from 'Api/AccountApi';
import { checkExistingUser } from 'Api/ProfileApi';
import config from 'Config/USStates.json';
import { getStoreState } from 'Utils/Store';

/** FORM FIELD VALIDATORS * */

export const required = (value) => {
  return value && value.trim().length > 0 ? undefined : 'Required';
};

export const requiredBoolean = (value) => {
  return value && value.trim().length > 0 ? undefined : true;
};

export const usState = (value) => {
  if (config.short.includes(value)) return undefined;
  return 'Must be a valid US state abbrev.';
};

export const minLength = (min) => (value) => {
  return value && value.length < min ? `Must be ${min} characters or more` : undefined;
};

export const minLength6 = minLength(6);
// if you want to reuse minLengh
// export const minLength8 = minLength(8)
// export const minLength12 = minLength(12)

export const number = (value) => (value && Number.isNaN(Number(value)) ? 'Must be a number' : undefined);

export const email = (value) => {
  return value && !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i.test(value) ? 'Invalid email address' : undefined;
};

export const compareToEmail = (value, allValues) => {
  const { email } = allValues;
  return value !== email ? 'Email does not match' : undefined;
};

export const password = (value) =>
  value.length < 6 ? 'Invalid. Password length needs to be at least 6 characters' : undefined;

export const compareToPassword = (value, allValues) => {
  const { newPass } = allValues;
  return value !== newPass ? "Your new password and confirm password don't match." : undefined;
};

export const dayOfMonth = (value) => {
  const number = Number(value);
  return number < 1 || number % 1 !== 0 || number > 31 ? 'Invalid day' : undefined;
};

export const lifetimeYear = (value) => {
  const number = Number(value);
  return number < 1900 || number % 1 !== 0 || number > new Date().getFullYear() ? 'Invalid year' : undefined;
};

export const passStrength = (values) => {
  /*
    const re = /(?=.*[A-Za-z])(?=.*\d)(?=.*[!@#$%^&*-+=~,.:;])[A-Za-z\d!@#$%^&*-+=~,.:;]{8,}/
    Rules:
    Invalid - has less than 6 chars - Color : Red
    Weak - Only meets the min 6-7 Chars and no numbers - Color: Red
    Medium - 8+ chars and has one of Uppercase, Special or Number - Color: Orange
    Strong - 8+ chars has all of (atleast one) Uppercase, special and number - Color: Green

    only post warning massage if users enter at lease 6 characters.
    If they enter less than 6 characters, it will be caught by error handler
    */
  let warnings = null;
  if (values && values.length > 5) {
    if (values.length >= 6 && values.length < 8) {
      warnings = 'Weak';
    } else if (values.length >= 8 && /[A-Z]/.test(values) && /[\d]/.test(values) && /[!@#$%^&*-+=~,.:;]/.test(values)) {
      warnings = 'Strong';
    } else {
      warnings = 'Medium';
    }
  }
  return warnings;
};

export const gender = (value) => (value && value !== 'Gender' ? undefined : 'Required');

// cvv should be 3 or 4 digits number
export const cvv = (value) => (value && !/^([0-9]{3}|[0-9]{4})$/.test(value) ? 'Invalid cvv' : undefined);

// zip should be 5 digits number
export const zip = (value) => (value && !/^([0-9]{5})$/.test(value) ? 'Invalid zip' : undefined);

// zip can be 5 digits number, or 5 digits number plus dash and 4 more digits.
export const shippingZip = (value) => (value && !/^[0-9]{5}(?:-[0-9]{4})?$/.test(value) ? 'Invalid zip' : undefined);

// expiration date: mm/yy
export const expiryDate = (value) =>
  value && !/^(0[1-9]|1[0-2]) \/ ?([0-9]{2})$/.test(value) ? 'Invalid expiration date' : undefined;

// birthdate: mm/dd/yyyy
export const birthdate = (value) => {
  if (!value) return undefined;
  if (/^([0-9]{2})\/?([0-9]{2})\/?([0-9]{4})$/.test(value)) {
    const dob =
      value.length === 8 ? new Date(`${value.slice(0, 2)}/${value.slice(2, 4)}/${value.slice(4, 8)}`) : new Date(value);
    if (dob !== 'Invalid Date' && dob <= new Date()) {
      return undefined;
    }
  }
  return 'Invalid birthdate';
};

/** FORM SUBMIT VALIDATORS * */

export const asyncValidateExistingUser = (_, { email }) => {
  const { isAuthenticated } = getStoreState('user');
  // only calling checkExistingUser when email is not undefined and user is not authenticated
  if (!isAuthenticated && email !== undefined) {
    const encodedEmail = encodeURIComponent(email);
    return checkExistingUser(encodedEmail).then((response) => {
      if (response.found) {
        const existingUser = { email: 'existing-user' };
        return Promise.reject(existingUser);
      }
      return Promise.resolve();
    });
  }
  return Promise.resolve();
};

export const asyncValidateAddress = (_, address) => {
  return new Promise((resolve, reject) => {
    verifyAddress(address).then((result) => {
      const { errors } = result;
      if (errors) {
        const formErrors = {};
        for (const field in errors) {
          formErrors[field] = errors[field];
        }
        return reject(formErrors);
      }
      return resolve();
    });
  });
};

// normalize birthday
export const normalizeBirthday = (value, previousValue) => {
  if (!value) {
    return value;
  }
  const onlyNums = value.replace(/[^\d]/g, '');
  if (!previousValue || value.length > previousValue.length) {
    // typing forward
    if (onlyNums.length === 2) {
      return `${onlyNums}/`;
    }
    if (onlyNums.length === 4) {
      return `${onlyNums.slice(0, 2)}/${onlyNums.slice(2)}/`;
    }
  }

  if (onlyNums.length <= 2) {
    return onlyNums;
  }
  if (onlyNums.length <= 4) {
    return `${onlyNums.slice(0, 2)}/${onlyNums.slice(2)}`;
  }
  return `${onlyNums.slice(0, 2)}/${onlyNums.slice(2, 4)}/${onlyNums.slice(4, 8)}`;
};

export const normalizeZip = (value) => {
  if (!value) {
    return value;
  }
  return value.replace(/[^\d]/g, '');
};

/**
 * hiddenTax validation is being used on payment form for payment review.
 */
export const hiddenTax = (value) => (value && value !== -1 ? undefined : true);

/*
Helpers specific to forms with useForm hook:
*/

let formErrorId = 0;
export const onSubmitReactFormError = (message, status = null, details = null) => {
  console.error(`Submit error: ${message}, ${status}, ${details}`);
  formErrorId++;
  return { formErrorId, message };
};
